<template>
  <div class="date-input-container">
    <FormLabel :label="label" :isRequired="isRequired && !readOnly" />
    <div class="date-input-wrapper">
      <!-- readonlyモードではテキスト表示 -->
      <div v-if="readOnly" class="readonly-text">
        {{ displayValue }}
      </div>
      <!-- 通常モードではflat-pickrを使用 -->
      <div v-else class="flat-pickr-wrapper relative">
        <feather-icon
          class="calendar-icon"
          icon="CalendarIcon"
          svgClasses="h-4 w-4"
        />
        <flat-pickr
          :class="['date-input-flat-picker', { error: errorMessage }]"
          v-model="selectedDate"
          :placeholder="placeholder"
          :config="configTimePicker"
          :disabled="disabled"
          @on-change="onChange"
        />
        <!-- クリアアイコン -->
        <feather-icon
          v-if="!disabled && value"
          class="clear-icon"
          icon="XIcon"
          svgClasses="h-4 w-4"
          @click.stop="clearDate"
        />
      </div>
    </div>
    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
    <InputBottomDivider v-if="readOnly" />
  </div>
</template>

<script>
import FormLabel from "./FormLabel.vue";
import InputBottomDivider from "./InputBottomDivider.vue";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { Japanese } from "flatpickr/dist/l10n/ja.js";

const FLAT_PICKR_CLASS_NAME = "flatpickr-calendar";
const DATE_FLAT_PICKR_CLASS_NAME = "date-input-flat-picker";
const STYLE_OVERWRITE = "style-overwrite";

export default {
  name: "DateInput",
  components: {
    FormLabel,
    InputBottomDivider,
    flatPickr,
  },
  props: {
    /**
     * ラベルテキスト
     * @type {String}
     * @required
     */
    label: {
      type: String,
      required: true,
    },
    /**
     * 入力値（YYYY-MM-DD形式）
     * @type {String}
     * @default ""
     */
    value: {
      type: String,
      default: "",
    },
    /**
     * プレースホルダー
     * @type {String}
     * @default ""
     */
    placeholder: {
      type: String,
      default: "日付を選択",
    },
    /**
     * 必須項目かどうか
     * @type {Boolean}
     * @default false
     */
    isRequired: {
      type: Boolean,
      default: false,
    },
    /**
     * 読み取り専用かどうか
     * @type {Boolean}
     * @default false
     */
    readOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * 無効化されているかどうか
     * @type {Boolean}
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * エラーメッセージ
     * @type {String}
     * @default ""
     */
    errorMessage: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      selectedDate: null,
      configTimePicker: {
        enableTime: false,
        dateFormat: "Y/m/d",
        locale: Japanese,
        monthSelectorType: "static",
        allowInput: false,
      },
    };
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal) {
          // YYYY-MM-DD形式の文字列をDateオブジェクトに変換
          const date = new Date(newVal);
          if (!isNaN(date.getTime())) {
            this.selectedDate = date;
          } else {
            this.selectedDate = null;
          }
        } else {
          this.selectedDate = null;
        }
      },
      immediate: true,
    },
  },
  computed: {
    displayValue() {
      if (
        this.value === null ||
        this.value === undefined ||
        this.value === ""
      ) {
        return "-";
      }
      // YYYY-MM-DD形式をYYYY年MM月DD日に変換
      const date = new Date(this.value);
      if (isNaN(date.getTime())) {
        return this.value;
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}/${month}/${day}`;
    },
  },
  methods: {
    onChange(selectedDates) {
      if (selectedDates && selectedDates.length > 0) {
        const date = selectedDates[0];
        // DateオブジェクトをYYYY-MM-DD形式の文字列に変換
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const dateString = `${year}-${month}-${day}`;
        this.$emit("input", dateString);
        this.$emit("blur", dateString);
      } else {
        this.$emit("input", "");
        this.$emit("blur", "");
      }
    },
    clearDate() {
      this.selectedDate = null;
      this.$emit("input", "");
      this.$emit("blur", "");
    },
  },
  mounted() {
    // flatpickrに独自スタイルを当てるためにclass付与
    this.$nextTick(() => {
      document.querySelectorAll(`.${FLAT_PICKR_CLASS_NAME}`).forEach(fc => {
        if (!fc.classList.contains(DATE_FLAT_PICKR_CLASS_NAME)) {
          fc.classList.add(DATE_FLAT_PICKR_CLASS_NAME, STYLE_OVERWRITE);
        }
      });
    });
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.date-input-container {
  display: flex;
  flex-direction: column;
  width: 100%;

  input {
    padding-left: 2.5rem;
    padding-top: 0.65rem;
    padding-bottom: 0.65rem;
    &.active {
      &:focus,
      &.active {
        border-color: #2d9cdb;
        box-shadow: 0 0 0 2px rgba(45, 156, 219, 0.1);
      }
    }
    &::placeholder {
      color: $aisea-mouse-gray;
    }
  }

  .date-input-wrapper {
    .readonly-text {
      padding: 0rem 0.75rem;
      font-family: $aisea-font-family;
      font-size: 16px;
      font-weight: 400;
      color: $aisea-dark-gray;
      line-height: 1.5;
      min-height: 26px;
    }

    .flat-pickr-wrapper {
      position: relative;

      .calendar-icon {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 18px;
        color: $aisea-dark-gray;
        pointer-events: none;
        z-index: 1;
      }

      .date-input-flat-picker {
        width: 100%;

        &.error {
          ::v-deep .flatpickr-input {
            border-color: $aisea-red;
            background-color: rgba($aisea-red, 0.1);
          }
        }

        ::v-deep .flatpickr-input {
          width: 100%;
          padding: 0.6rem 2.25rem;
          padding-left: 2.5rem;
          font-family: $aisea-font-family;
          font-size: 14px;
          font-weight: 400;
          color: $aisea-dark-gray;
          background-color: $aisea-pure-white;
          border: 1px solid $aisea-mouse-gray;
          border-radius: 6px;
          outline: none;
          transition: border-color 0.3s ease, box-shadow 0.3s ease;
          min-height: 36px;

          &:focus,
          &.active {
            border-color: #2d9cdb;
            box-shadow: 0 0 0 2px rgba(45, 156, 219, 0.1);
          }

          &:disabled {
            background-color: #f5f5f5;
            color: $aisea-mouse-gray;
            cursor: not-allowed;
          }
        }
      }
      .clear-icon {
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 16px;
        color: $aisea-mouse-gray;
        cursor: pointer;
        z-index: 2;

        &:hover {
          color: $aisea-dark-gray;
        }
      }
    }

    .error-message {
      margin-top: 0.25rem;
      padding-left: 0.25rem;
      font-family: $aisea-font-family;
      font-size: 12px;
      font-weight: 400;
      color: $aisea-red;
      line-height: 1.5;
    }
  }
}
</style>

<style lang="scss">
@import "src/assets/scss/aisea/variables";

.flatpickr-calendar {
  &.date-input-flat-picker {
    .flatpickr-current-month {
      display: flex;
      justify-content: center;
      align-items: center;
      align-items: baseline;
      gap: 30px;

      span.cur-month {
        color: #626262;
      }
    }
    .flatpickr-day {
      &.selected,
      &.startRange,
      &.endRange,
      &.selected.inRange,
      &.startRange.inRange,
      &.endRange.inRange,
      &.selected:focus,
      &.startRange:focus,
      &.endRange:focus,
      &.selected:hover,
      &.startRange:hover,
      &.endRange:hover,
      &.selected.prevMonthDay,
      &.startRange.prevMonthDay,
      &.endRange.prevMonthDay,
      &.selected.nextMonthDay,
      &.startRange.nextMonthDay,
      &.endRange.nextMonthDay {
        background: $aisea-blue !important;
        -webkit-box-shadow: none;
        box-shadow: none;
        border-color: $aisea-blue !important;
      }
    }
  }
}
</style>
